import { type ReactNode } from "react";
import type { GenericRecord } from "@domain/models/GenericRecord";
import type { Payment, PaymentPrototype } from "@domain/models/Payment";

export enum TableCellAlignment {
    left = "left",
    center = "center",
    right = "right",
}

export interface TableItem<E> {
    key: any;
    item: E;
    children: E[];
    extra?: unknown;
}

/**
 * A problem found on a single row while previewing a DATEV import (FIBU-504).
 *
 * `code` is a stable key the UI localizes - the message never travels as text, so the same
 * diagnostic renders in de/en/ru. Rows carrying any of these are shown in the preview but
 * excluded from the import.
 */
export interface ImportRowError {
    code: "missingBank" | "missingKasse";
    /** The account number that could not be resolved, for the tooltip text. */
    value?: string;
}

export interface TableItemExtraContainer {
    payments?: Map<GenericRecord, Payment[]>;
    isVirtualRecord?: boolean;
    /** DATEV import preview only: why this row cannot be imported (FIBU-504). */
    importErrors?: ImportRowError[];
    /** DATEV import preview only: row already saved in this session (FIBU-940). */
    imported?: boolean;
}

export interface TableItemExtra<I, E = Record<string, any>> extends TableItem<I> {
    extra?: TableItemExtraContainer & E;
}

export type GenericRecordTableItem = TableItemExtra<GenericRecord>;

export interface TableColumn<E> {
    key?: string;
    width: number;
    widthConstraints?: {
        min: number;
        max: number;
    };
    header: string | ReactNode;
    footer?: {
        mainFooter: ReactNode;
        selectedFooter: ReactNode;
    };
    getter: (tableItem: TableItem<E>) => any;
    sortable?: boolean;
    sortFunc?: (a: TableItem<E>, b: TableItem<E>) => number;
    sortDirection?: "asc" | "desc" | null;
    alignment?: TableCellAlignment;
    className?: string;
    isMoney?: boolean;
    resizable?: boolean;
    onResize?: (width: number) => void;
}

export type TableRowData = GenericRecordTableItem & {
    isFocused?: boolean;
    rowSaldo?: number | null;
};

export interface AccountingTableContext {
    onPaymentOpen: (recordId: string) => void;
    onDocumentOpen: (recordId: string) => void;
    onBindingSave: (
        record: GenericRecord,
        connections: { paymentSourceRecord: GenericRecord; payment: PaymentPrototype }[] | null,
    ) => void;
}

export type PinnedRowData =
    | { _pinnedRowType: "saldoHeader"; _saldoValue: number }
    | { _pinnedRowType: "selected" }
    | { _pinnedRowType: "main" };
